import { useSyncExternalStore, useCallback, useMemo, useRef } from 'react';

/**
 * Hook for persisting state to localStorage with cross-component synchronization.
 *
 * Uses `useSyncExternalStore` to ensure all components using the same key
 * stay in sync, both within the same tab (via custom event) and across tabs
 * (via storage event).
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((val: T) => T)) => void] {
  // Serialize initialValue once to avoid recreating callbacks when initialValue is object/array
  const initialValueRef = useRef(initialValue);
  const serializedInitial = useMemo(
    () => JSON.stringify(initialValueRef.current),
    [],
  );

  // Subscribe to storage changes (from other tabs AND same tab via custom event)
  const subscribe = useCallback(
    (callback: () => void) => {
      // Listen for cross-tab storage events
      const handleStorage = (e: StorageEvent) => {
        if (e.key === key) callback();
      };

      // Listen for same-tab updates via custom event
      const handleCustom = (e: Event) => {
        const customEvent = e as CustomEvent<{ key: string }>;
        if (customEvent.detail.key === key) callback();
      };

      window.addEventListener('storage', handleStorage);
      window.addEventListener('local-storage-update', handleCustom);

      return () => {
        window.removeEventListener('storage', handleStorage);
        window.removeEventListener('local-storage-update', handleCustom);
      };
    },
    [key],
  );

  // Get current value from localStorage
  const getSnapshot = useCallback(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ?? serializedInitial;
    } catch {
      return serializedInitial;
    }
  }, [key, serializedInitial]);

  // SSR fallback
  const getServerSnapshot = useCallback(() => {
    return serializedInitial;
  }, [serializedInitial]);

  const storedString = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );

  // Memoize the parsed value to maintain referential stability
  // This prevents infinite re-renders when the value is used in dependency arrays
  const storedValue = useMemo(
    () => JSON.parse(storedString) as T,
    [storedString],
  );

  // Setter that updates localStorage AND dispatches event for same-tab sync
  const setValue = useCallback(
    (value: T | ((val: T) => T)) => {
      try {
        const currentValue = JSON.parse(
          window.localStorage.getItem(key) ?? serializedInitial,
        ) as T;
        const valueToStore =
          value instanceof Function ? value(currentValue) : value;

        window.localStorage.setItem(key, JSON.stringify(valueToStore));

        // Dispatch custom event for same-tab components
        window.dispatchEvent(
          new CustomEvent('local-storage-update', { detail: { key } }),
        );
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key, serializedInitial],
  );

  return [storedValue, setValue];
}
